import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiUser, FiMail } from 'react-icons/fi';
import toast from 'react-hot-toast';
import NoteIcon from '../common/icons/NoteIcon';
import ChartIcon from '../common/icons/ChartIcon';
import BullseyeIcon from '../common/icons/BullseyeIcon';
import { calculateOrderPricing } from '../../utils/order';

const EMPTY_FORM_STATE = {
  first_name: '',
  last_name: '',
  email: ''
};

// Predvyplní formulár len v development režime, aby bolo testovanie rýchlejšie.
const DEV_FORM_DEFAULTS = {
  first_name: 'Joe',
  last_name: 'Don',
  email: 'joe.don@gmail.com'
};

const getInitialFormData = () =>
  import.meta.env.DEV
    ? { ...EMPTY_FORM_STATE, ...DEV_FORM_DEFAULTS }
    : { ...EMPTY_FORM_STATE };

const OrderForm = ({ onSubmit, isLoading, selectedTickets, ticketPrice = 5 }) => {
  const effectiveTicketPrice = Number(ticketPrice) > 0 ? Number(ticketPrice) : 5;
  const [formData, setFormData] = useState(getInitialFormData);
  const [errors, setErrors] = useState({});
  const [isEmailConfirmOpen, setIsEmailConfirmOpen] = useState(false);
  const [pendingSubmitData, setPendingSubmitData] = useState(null);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name.trim()) {
      newErrors.firstName = 'Meno je povinné';
    }

    if (!formData.last_name.trim()) {
      newErrors.lastName = 'Priezvisko je povinné';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email je povinný';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Neplatný email formát';
    }

    if (selectedTickets.length === 0) {
      newErrors.tickets = 'Musíte vybrať aspoň jeden lístok';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Prosím, opravte chyby vo formulári');
      return;
    }

    const ticketCount = selectedTickets.length;
    const pricing = calculateOrderPricing(ticketCount, effectiveTicketPrice);
    const totalAmount = pricing.totalAmount;

    const dataToSend = {
      ...formData,
      ticket_count: ticketCount,
      total_amount: totalAmount,
      tickets: selectedTickets // ak je potrebné
    };

    setPendingSubmitData(dataToSend);
    setIsEmailConfirmOpen(true);
  };

  const confirmEmailAndSubmit = () => {
    if (!pendingSubmitData) return;
    console.log('Odosielam tieto dáta do Supabase:', pendingSubmitData);
    onSubmit(pendingSubmitData);
    setIsEmailConfirmOpen(false);
    setPendingSubmitData(null);
  };

  const closeEmailConfirm = () => {
    setIsEmailConfirmOpen(false);
    setPendingSubmitData(null);
  };

  const handleChange = (field) => (e) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const pricing = calculateOrderPricing(selectedTickets.length, effectiveTicketPrice);
  const total = pricing.totalAmount;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="card backdrop-blur-sm"
    >
      <div className="mb-6">
        <h3 className="text-2xl font-bold text-primary-100 mb-2 flex items-center gap-2">
          <NoteIcon className="text-primary-300" aria-hidden="true" />
          <span>Kontaktné údaje</span>
        </h3>
        <p className="text-slate-300">
          Vyplňte vaše údaje pre dokončenie objednávky
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* First Name */}
          <div>
            <label className="block text-sm font-semibold text-slate-200 mb-2">
              <FiUser className="inline w-4 h-4 mr-1" />
              Meno *
            </label>
            <input
              type="text"
              value={formData.first_name}
              onChange={handleChange('first_name')}
              className={`input ${errors.firstName ? 'input-error' : ''}`}
              placeholder="Vaše meno"
              required
            />
            {errors.firstName && (
              <p className="text-error-200 text-sm mt-1">{errors.firstName}</p>
            )}
          </div>

          {/* Last Name */}
          <div>
            <label className="block text-sm font-semibold text-slate-200 mb-2">
              <FiUser className="inline w-4 h-4 mr-1" />
              Priezvisko *
            </label>
            <input
              type="text"
              value={formData.last_name}
              onChange={handleChange('last_name')}
              className={`input ${errors.lastName ? 'input-error' : ''}`}
              placeholder="Vaše priezvisko"
              required
            />
            {errors.lastName && (
              <p className="text-error-200 text-sm mt-1">{errors.lastName}</p>
            )}
          </div>
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-semibold text-slate-200 mb-2">
            <FiMail className="inline w-4 h-4 mr-1" />
            Email *
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={handleChange('email')}
            className={`input ${errors.email ? 'input-error' : ''}`}
            placeholder="vas@email.com"
            required
          />
          {errors.email && (
            <p className="text-error-200 text-sm mt-1">{errors.email}</p>
          )}
          <p className="text-slate-400 text-sm mt-1">
            Na tento email vám príde QR kód na potvrdenie platby
          </p>
          <p className="text-primary-200 text-lg mt-1 font-semibold">
            Akcia: kúp 5 lístkov, 1 máš zdarma.
          </p>
        </div>

        {/* Order Summary */}
        <div className="bg-slate-800/60 rounded-xl p-4 border border-slate-700">
          <h4 className="font-semibold text-primary-100 mb-3 flex items-center gap-2">
            <ChartIcon className="text-primary-300" aria-hidden="true" />
            <span>Súhrn objednávky</span>
          </h4>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-slate-300">Počet lístkov:</span>
              <span className="font-semibold text-slate-100">{selectedTickets.length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-300">Lístky zdarma:</span>
              <span className="font-semibold text-success-200">{pricing.freeTickets}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-300">Platené lístky:</span>
              <span className="font-semibold text-slate-100">{pricing.payableTickets}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-300">Cena za platený lístok:</span>
              <span className="font-semibold text-slate-100">{effectiveTicketPrice}€</span>
            </div>
            <div className="border-t border-slate-700 pt-2 flex justify-between text-lg">
              <span className="font-bold text-slate-100">Celkom:</span>
              <span className="font-bold text-primary-200">{total}€</span>
            </div>
            {pricing.ticketCount > 0 && pricing.ticketCount % 6 === 5 && (
              <p className="text-warning-200 text-sm font-semibold">
                Vyber si ešte 1 lístok a máš 1 zdarma.
              </p>
            )}
          </div>
        </div>

        {errors.tickets && (
          <div className="bg-error-500/10 border border-error-500/40 rounded-xl p-4">
            <p className="text-error-200 font-medium">{errors.tickets}</p>
          </div>
        )}

        {/* Submit Button */}
        <motion.button
          type="submit"
          disabled={isLoading || selectedTickets.length === 0}
          className="w-full btn btn-primary text-lg py-4 disabled:opacity-50 disabled:cursor-not-allowed"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          {isLoading ? (
            <div className="flex items-center justify-center gap-2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-100"></div>
              Vytváranie objednávky...
            </div>
          ) : (
            <span className="inline-flex items-center justify-center gap-2">
              <BullseyeIcon aria-hidden="true" />
              <span>Vytvoriť objednávku za {total}€</span>
            </span>
          )}
        </motion.button>

        <div className="text-center text-sm text-slate-400">
          <p>Po vytvorení objednávky dostanete email s QR kódom.</p>
          <p>Ukážte QR kód organizátorovi a zaplaťte v hotovosti.</p>
        </div>
      </form>

      {isEmailConfirmOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
          <div className="w-full max-w-md rounded-2xl border border-slate-700 bg-slate-900 p-6 shadow-2xl">
            <h4 className="text-lg font-semibold text-primary-100 mb-2">
              Potvrdenie emailu
            </h4>
            <p className="text-slate-300 mb-4">
              Skontrolujte prosím email pred odoslaním objednávky:
            </p>
            <p className="rounded-lg border border-primary-500/30 bg-primary-500/10 px-3 py-2 text-primary-100 font-semibold break-all">
              {formData.email}
            </p>
            <p className="text-sm text-slate-400 mt-3">
              Na tento email odošleme potvrdenie objednávky a QR kód.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeEmailConfirm}
                className="btn btn-secondary"
                disabled={isLoading}
              >
                Upraviť email
              </button>
              <button
                type="button"
                onClick={confirmEmailAndSubmit}
                className="btn btn-primary"
                disabled={isLoading}
              >
                Potvrdiť a odoslať
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default OrderForm;
